import React, { useState, useEffect } from 'react';
import { EventTemplate } from '../../../types';

interface EventTemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Partial<EventTemplate>) => void;
  template?: EventTemplate | null;
  mode: 'create' | 'edit';
}

interface FormErrors {
  default_booking_code?: string;
  name?: string;
  min_nights?: string;
  max_nights?: string;
}

const BOOKING_CODE_PATTERN = /^[A-Z0-9_-]{4,20}$/;

const EventTemplateForm: React.FC<EventTemplateFormProps> = ({
  isOpen,
  onClose,
  onSave,
  template,
  mode
}) => {
  const [formData, setFormData] = useState<Partial<EventTemplate>>({
    default_booking_code: '',
    name: '',
    description: '',
    min_nights: 1,
    max_nights: undefined,
    max_sites_per_booking: 1,
    stay_buffer_days: undefined,
    default_price: undefined,
    sites_blocked: true,
    active: true
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when opening or template changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && template) {
        setFormData({
          default_booking_code: template.default_booking_code,
          name: template.name,
          description: template.description || '',
          min_nights: template.min_nights,
          max_nights: template.max_nights,
          max_sites_per_booking: template.max_sites_per_booking,
          stay_buffer_days: template.stay_buffer_days,
          default_price: template.default_price,
          sites_blocked: template.sites_blocked,
          active: template.active
        });
      } else {
        // Reset to defaults for create mode
        setFormData({
          default_booking_code: '',
          name: '',
          description: '',
          min_nights: 1,
          max_nights: undefined,
          max_sites_per_booking: 1,
          stay_buffer_days: undefined,
          default_price: undefined,
          sites_blocked: true,
          active: true
        });
      }
      setErrors({});
    }
  }, [isOpen, template, mode]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    let processedValue: string | number | boolean | undefined = value;

    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = value === '' ? undefined : parseFloat(value);
    } else if (name === 'default_booking_code') {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Booking code validation
    if (!formData.default_booking_code) {
      newErrors.default_booking_code = 'Booking code is required';
    } else if (!BOOKING_CODE_PATTERN.test(formData.default_booking_code)) {
      newErrors.default_booking_code = 'Code must be 4-20 characters (A-Z, 0-9, hyphen, underscore)';
    }

    // Name validation
    if (!formData.name?.trim()) {
      newErrors.name = 'Template name is required';
    }

    // Night limits validation
    if (formData.min_nights && formData.max_nights) {
      if (formData.max_nights < formData.min_nights) {
        newErrors.max_nights = 'Max nights must be >= min nights';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean up empty optional fields
      const cleanedData: Partial<EventTemplate> = {
        ...formData,
        description: formData.description || undefined
      };

      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content event-form-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Create Event Template' : 'Edit Template'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="event-form">
          <div className="form-body">
            <div className="form-row">
              {/* Booking Code */}
              <div className="form-group">
                <label htmlFor="default_booking_code">Default Booking Code *</label>
                <input
                  type="text"
                  id="default_booking_code"
                  name="default_booking_code"
                  value={formData.default_booking_code || ''}
                  onChange={handleChange}
                  placeholder="e.g., MUSICFEST"
                  maxLength={20}
                  className={errors.default_booking_code ? 'error' : ''}
                />
                {errors.default_booking_code && <span className="form-error">{errors.default_booking_code}</span>}
                <span className="form-hint">Used as prefix for event instances</span>
              </div>

              {/* Template Name */}
              <div className="form-group">
                <label htmlFor="name">Template Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  placeholder="e.g., Annual Music Festival"
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                placeholder="Optional template description..."
                rows={2}
              />
            </div>

            <div className="form-section">
              <h3>Default Booking Rules</h3>
              <div className="form-row form-row-4">
                <div className="form-group">
                  <label htmlFor="min_nights">Min Nights</label>
                  <input
                    type="number"
                    id="min_nights"
                    name="min_nights"
                    value={formData.min_nights ?? ''}
                    onChange={handleChange}
                    min={1}
                    className={errors.min_nights ? 'error' : ''}
                  />
                  {errors.min_nights && <span className="form-error">{errors.min_nights}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="max_nights">Max Nights</label>
                  <input
                    type="number"
                    id="max_nights"
                    name="max_nights"
                    value={formData.max_nights ?? ''}
                    onChange={handleChange}
                    min={formData.min_nights || 1}
                    className={errors.max_nights ? 'error' : ''}
                  />
                  {errors.max_nights && <span className="form-error">{errors.max_nights}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="max_sites_per_booking">Max Sites/Booking</label>
                  <input
                    type="number"
                    id="max_sites_per_booking"
                    name="max_sites_per_booking"
                    value={formData.max_sites_per_booking ?? 1}
                    onChange={handleChange}
                    min={1}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="default_price">Default Price ($)</label>
                  <input
                    type="number"
                    id="default_price"
                    name="default_price"
                    value={formData.default_price ?? ''}
                    onChange={handleChange}
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Site & Booking Settings</h3>
              <div className="form-row">
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="sites_blocked"
                      checked={formData.sites_blocked ?? true}
                      onChange={handleChange}
                    />
                    Block sites for event only
                  </label>
                  <span className="form-hint">Only assigned sites visible for event booking (recommended)</span>
                </div>

                <div className="form-group">
                  <label htmlFor="stay_buffer_days">Stay Buffer (days)</label>
                  <input
                    type="number"
                    id="stay_buffer_days"
                    name="stay_buffer_days"
                    value={formData.stay_buffer_days ?? ''}
                    onChange={handleChange}
                    min={0}
                    placeholder="0"
                  />
                  <span className="form-hint">Days before/after event dates customers can book</span>
                </div>
              </div>

              {mode === 'edit' && (
                <div className="form-group form-checkbox" style={{ marginTop: 'var(--spacing-md)' }}>
                  <label>
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active ?? true}
                      onChange={handleChange}
                    />
                    Template is active
                  </label>
                  <span className="form-hint">Inactive templates cannot create new events</span>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Template' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventTemplateForm;
